import { FormEvent, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Fade,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  keyframes,
} from "@mui/material";
import {
  CheckRounded as CheckIcon,
  LockOutlined as LockIcon,
  NotificationsActiveOutlined as BellIcon,
} from "@mui/icons-material";
import { isAxiosError } from "axios";
import useApi from "@/hooks/useApi";
import { useBranding } from "../../branding/BrandingProvider";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 currentColor; }
  70%  { box-shadow: 0 0 0 8px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

const rise = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/**
 * What `/i/new-patient` shows while patient self-signup is closed (`runConfig.signupBlocked`,
 * the TeleHeadache cells before launch). The marketing site sends people here; instead of an account they leave an
 * email address for the launch announcement (POST /launch-waitlist). The API refuses the signup
 * endpoints on its own while the flag is on — this page is the front door, not the lock.
 */
export default function LaunchWaitlist() {
  const brand = useBranding();
  const api = useApi(false);
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [joinedAs, setJoinedAs] = useState<string | null>(null);

  const websiteHost = brand.websiteUrl?.replace(/^https?:\/\//, "").replace(/\/$/, "");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setFieldError("Please enter a valid email address.");
      return;
    }
    setFieldError(null);
    setSubmitError(null);
    setSubmitting(true);
    try {
      // The page shows its own error; the global dialog would stack a second one on top.
      await api.post("/launch-waitlist", { email: trimmed }, { suppressErrorDialog: true });
      setJoinedAs(trimmed);
    } catch (err) {
      setSubmitError(
        isAxiosError(err) && err.response?.status === 429
          ? "Too many attempts from this network. Please try again in a few minutes."
          : "We couldn't save your email just now. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      component="main"
      sx={(theme) => ({
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(140deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 45%, ${theme.palette.primary.main} 100%)`,
      })}
    >
      {/* Soft light shapes behind the card — decoration only. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          "& > span": {
            position: "absolute",
            borderRadius: "50%",
            background: (theme) =>
              `radial-gradient(circle, ${alpha(theme.palette.brand.onHero, 0.16)} 0%, ${alpha(theme.palette.brand.onHero, 0)} 70%)`,
          },
        }}
      >
        <Box component="span" sx={{ width: 520, height: 520, top: -180, right: -140 }} />
        <Box component="span" sx={{ width: 420, height: 420, bottom: -160, left: -120 }} />
        <Box component="span" sx={{ width: 220, height: 220, top: "38%", left: "8%", opacity: 0.6 }} />
      </Box>

      <Container
        maxWidth={false}
        sx={{
          maxWidth: 680,
          position: "relative",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: { xs: 5, sm: 8 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box
          component="img"
          src={brand.logoWhite}
          alt={brand.displayName}
          sx={{ height: { xs: 80, sm: 112 }, maxWidth: "80%", objectFit: "contain", mb: { xs: 3, sm: 4 } }}
        />

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            borderRadius: 4,
            p: { xs: 3, sm: 5 },
            boxShadow: (theme) => theme.palette.brand.shadow.raised,
            animation: `${rise} 600ms ease-out both`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          {joinedAs === null ? (
            <Box component="form" noValidate onSubmit={handleSubmit}>
              <Box
                sx={(theme) => ({
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  mb: 2.5,
                  borderRadius: 999,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.dark",
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                })}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    color: "primary.light",
                    animation: `${pulse} 2s infinite`,
                    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                  }}
                />
                Launching soon
              </Box>

              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: 28, sm: 36 },
                  lineHeight: 1.15,
                  fontWeight: 800,
                  letterSpacing: -0.5,
                  color: "secondary.dark",
                  mb: 1.5,
                }}
              >
                We're opening our doors soon
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: { xs: 16, sm: 17 }, lineHeight: 1.6, mb: 3.5 }}>
                {brand.displayName} is getting ready to welcome its first patients. Leave your email
                address and we'll let you know the moment you can create your account.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "flex-start" } }}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email address"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldError) setFieldError(null);
                  }}
                  error={fieldError !== null}
                  helperText={fieldError ?? " "}
                  disabled={submitting}
                  slotProps={{ htmlInput: { maxLength: 254 } }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <BellIcon />}
                  sx={{
                    flexShrink: 0,
                    height: 56,
                    px: 3.5,
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: 16,
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Notify me
                </Button>
              </Stack>

              {submitError && (
                <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>
                  {submitError}
                </Alert>
              )}

              <Stack direction="row" spacing={1} sx={{ alignItems: "center", mt: submitError ? 2 : 0.5, color: "text.secondary" }}>
                <LockIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2">
                  We'll only use your email to tell you when we launch.
                </Typography>
              </Stack>
            </Box>
          ) : (
            <Fade in timeout={400}>
              <Box sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }} role="status">
                <Box
                  sx={(theme) => ({
                    width: 72,
                    height: 72,
                    mx: "auto",
                    mb: 3,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.14),
                    color: "primary.dark",
                  })}
                >
                  <CheckIcon sx={{ fontSize: 40 }} />
                </Box>
                <Typography
                  component="h1"
                  sx={{ fontSize: { xs: 26, sm: 32 }, fontWeight: 800, letterSpacing: -0.5, color: "secondary.dark", mb: 1.5 }}
                >
                  You're on the list
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: 17, lineHeight: 1.6 }}>
                  Thank you. We'll email{" "}
                  <Box component="strong" sx={{ color: "text.primary", wordBreak: "break-all" }}>
                    {joinedAs}
                  </Box>{" "}
                  as soon as {brand.displayName} opens.
                </Typography>
              </Box>
            </Fade>
          )}
        </Paper>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 3 }}
          sx={{ alignItems: "center", mt: 4, color: (theme) => alpha(theme.palette.brand.onHero, 0.88), fontSize: 15 }}
        >
          <Box>
            Already have an account?{" "}
            <Link component={RouterLink} to="/login" sx={{ color: "brand.onHero", fontWeight: 700 }}>
              Sign in
            </Link>
          </Box>
          {brand.websiteUrl && (
            <Link href={brand.websiteUrl} sx={{ color: "brand.onHero", fontWeight: 600 }}>
              Visit {websiteHost}
            </Link>
          )}
        </Stack>
      </Container>

      <Typography
        component="footer"
        variant="body2"
        sx={{ position: "relative", textAlign: "center", pb: 3, color: (theme) => alpha(theme.palette.brand.onHero, 0.7) }}
      >
        © {new Date().getFullYear()} {brand.copyrightHolder}
      </Typography>
    </Box>
  );
}
